import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, Store } from "lucide-react";
import { fetchAdminStoreLinks, saveStoreLinks, formatApiError } from "../../lib/api";

const HTTPS_RE = /^https:\/\/[^\s/?#@]+\.[^\s/?#@]+(\S*)$/;
const ERR = "Please enter a full link starting with https://";
const FIELDS = [
  { key: "ios_url", label: "App Store link", placeholder: "https://apps.apple.com/…" },
  { key: "android_url", label: "Google Play link", placeholder: "https://play.google.com/store/apps/…" },
];

export const StoreLinksPanel = ({ token, onUnauthorized }) => {
  const [links, setLinks] = useState({ ios_url: "", android_url: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const dirty = useRef(false);

  useEffect(() => {
    fetchAdminStoreLinks(token)
      .then(({ data }) => {
        if (!dirty.current) setLinks({ ios_url: data.ios_url || "", android_url: data.android_url || "" });
      })
      .catch((err) => err.response?.status === 401 && onUnauthorized())
      .finally(() => setLoaded(true));
  }, [token, onUnauthorized]);

  const save = async (e) => {
    e.preventDefault();
    setSaved(false);
    const errs = Object.fromEntries(FIELDS.filter((f) => links[f.key].trim() && !HTTPS_RE.test(links[f.key].trim())).map((f) => [f.key, ERR]));
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const { data } = await saveStoreLinks(token, links);
      setLinks({ ios_url: data.ios_url || "", android_url: data.android_url || "" });
      setSaved(true);
    } catch (err) {
      if (err.response?.status === 401) return onUnauthorized();
      setErrors(err.response?.data?.detail?.errors || { form: formatApiError(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} noValidate data-testid="store-links-panel" className="warm-card mt-10 p-6 sm:p-8 hover:translate-y-0">
      <p className="eyebrow">Store links</p>
      <h2 className="mt-2 font-serif text-2xl text-[#2C221E]">Where families can download Woven</h2>
      <p className="mt-2 text-sm text-[#5C4F4A]">Used by the launch email and the "Woven is here!" buttons. Leave empty until the listing exists.</p>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-sm font-medium text-[#2C221E]">{f.label}</span>
            <input
              type="url"
              value={links[f.key]}
              placeholder={loaded ? f.placeholder : "Loading saved link…"}
              disabled={!loaded}
              onChange={(e) => { dirty.current = true; setLinks({ ...links, [f.key]: e.target.value }); setErrors({ ...errors, [f.key]: "" }); setSaved(false); }}
              aria-invalid={!!errors[f.key]}
              data-testid={`store-links-${f.key.replace("_url", "")}-input`}
              className={`mt-2 w-full rounded-2xl border bg-[#FDFBF7] px-4 py-3 text-base text-[#2C221E] outline-none transition-colors focus:border-[#C85A32] ${errors[f.key] ? "border-[#B24E2A]" : "border-[#E6DEC6]"}`}
            />
            {errors[f.key] && <span role="alert" data-testid={`store-links-${f.key.replace("_url", "")}-error`} className="mt-2 block text-sm text-[#B24E2A]">{errors[f.key]}</span>}
          </label>
        ))}
      </div>
      {errors.form && <p role="alert" className="mt-3 text-sm text-[#B24E2A]">{errors.form}</p>}
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button type="submit" disabled={saving || !loaded} data-testid="store-links-save-button" className="btn-primary">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Store className="h-4 w-4" />} Save links
        </button>
        {saved && <span data-testid="store-links-saved" className="flex items-center gap-2 text-sm text-[#2F4537]"><CheckCircle2 className="h-4 w-4" /> Saved</span>}
      </div>
    </form>
  );
};
